// Versionshistorik för dokument: varje sparning skapar en ögonblicksbild
// (databastrigger) som går att förhandsgranska och återställa.
import { supabase } from "@/integrations/supabase/client";

export type VersionedDocumentType =
  | "risk_assessment"
  | "skr_assessment"
  | "work_preparation"
  | "amp_section";

const VERSION_TABLES: Record<VersionedDocumentType, string> = {
  risk_assessment: "folder_risk_assessments",
  skr_assessment: "skr_assessments",
  work_preparation: "work_preparations",
  amp_section: "amp_sections",
};

export const VERSION_LABELS: Record<VersionedDocumentType, string> = {
  risk_assessment: "Risksammanställning",
  skr_assessment: "SKR-bedömning",
  work_preparation: "Arbetsberedning",
  amp_section: "Avsnitt i arbetsmiljöplanen",
};

export interface DocumentVersion {
  id: string;
  version: number;
  created_at: string;
  created_by: string | null;
  snapshot: Record<string, unknown>;
  actor_name?: string | null;
}

/** Fält som aldrig skrivs tillbaka vid återställning. */
const IMMUTABLE_FIELDS = new Set([
  "id",
  "project_id",
  "created_at",
  "created_by",
  "updated_at",
  "deleted_at",
  "deleted_by",
  "delete_reason",
  "assessment_id",
]);

export interface VersionTarget {
  type: VersionedDocumentType;
  id: string;
  name?: string;
  docNumber?: string;
  projectId?: string | null;
}

export async function fetchDocumentVersions(target: VersionTarget): Promise<DocumentVersion[]> {
  const { data, error } = await supabase
    .from("document_versions")
    .select("id, version, created_at, created_by, snapshot")
    .eq("document_type", target.type)
    .eq("record_id", target.id)
    .order("version", { ascending: false })
    .limit(100);

  if (error) {
    console.error("Kunde inte hämta versioner", error);
    return [];
  }

  const rows = (data ?? []) as unknown as DocumentVersion[];
  const userIds = [...new Set(rows.map((r) => r.created_by).filter(Boolean))] as string[];
  if (userIds.length === 0) return rows;

  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, first_name, last_name")
    .in("id", userIds);

  const names = new Map(
    ((profiles ?? []) as { id: string; first_name?: string; last_name?: string }[]).map((p) => [
      p.id,
      [p.first_name, p.last_name].filter(Boolean).join(" ") || null,
    ]),
  );
  return rows.map((r) => ({ ...r, actor_name: r.created_by ? names.get(r.created_by) ?? null : null }));
}

/** Kort sammanfattning av vad som skiljer en version mot nuvarande innehåll. */
export function diffFields(
  snapshot: Record<string, unknown>,
  current: Record<string, unknown> | null,
): string[] {
  if (!current) return [];
  const changed: string[] = [];
  for (const key of Object.keys(snapshot)) {
    if (IMMUTABLE_FIELDS.has(key)) continue;
    if (JSON.stringify(snapshot[key]) !== JSON.stringify(current[key])) changed.push(key);
  }
  return changed;
}

export async function fetchCurrentRecord(
  target: VersionTarget,
): Promise<Record<string, unknown> | null> {
  const { data } = await supabase
    .from(VERSION_TABLES[target.type] as never)
    .select("*")
    .eq("id", target.id)
    .maybeSingle();
  return (data as Record<string, unknown>) ?? null;
}

/**
 * Skriver tillbaka innehållet från en tidigare version. Ingenting skrivs över
 * permanent – nuvarande innehåll sparas automatiskt som en ny version först.
 */
export async function restoreDocumentVersion(
  target: VersionTarget,
  version: DocumentVersion,
): Promise<void> {
  const payload: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(version.snapshot ?? {})) {
    if (IMMUTABLE_FIELDS.has(key)) continue;
    payload[key] = value;
  }
  if (Object.keys(payload).length === 0) return;
  payload.updated_at = new Date().toISOString();

  const { data: auth } = await supabase.auth.getUser();
  const userId = auth.user?.id ?? null;
  if ("updated_by" in (version.snapshot ?? {})) payload.updated_by = userId;

  const { error } = await supabase
    .from(VERSION_TABLES[target.type] as never)
    .update(payload as never)
    .eq("id", target.id);

  if (error) throw error;

  let actorName: string | null = null;
  if (userId) {
    const { data } = await supabase
      .from("user_profiles")
      .select("first_name, last_name")
      .eq("id", userId)
      .maybeSingle();
    const p = data as { first_name?: string; last_name?: string } | null;
    actorName = p ? [p.first_name, p.last_name].filter(Boolean).join(" ") || null : null;
  }

  if (target.projectId) {
    await supabase.from("document_audit_log").insert({
      project_id: target.projectId,
      document_type: target.type,
      doc_number: target.docNumber ?? null,
      table_name: VERSION_TABLES[target.type],
      record_id: target.id,
      action: "restored_version",
      actor_id: userId,
      actor_name: actorName,
      changes: { version: { from: null, to: version.version } },
    });
  }
}
